/**
 * Visor 3D de los DEMOS de OPUS ci. Es propio de los demos (el visor de /planos no se toca): carga uno o
 * varios IFC públicos por URL, los mide con la MISMA medición de ci (`crearMedidor`) y deja que cada demo
 * pinte cada elemento con una función (`pintar`). Mismo motor que el visor real: three + web-ifc por CDN.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Spinner } from '@fluentui/react-components';
import { crearMedidor } from '../../lib/medicionIfc.js';
import { getParametrosMedicionIfc } from '../../api/calibracion.js';
import { leerRelaciones, type ElementoDemo, type ModeloDemo } from './modeloDemo.js';

const THREE_URL = 'https://esm.sh/three@0.160.0';
const ORBIT_URL = 'https://esm.sh/three@0.160.0/examples/jsm/controls/OrbitControls.js';
const WEBIFC_URL = 'https://esm.sh/web-ifc@0.0.57';
const WEBIFC_WASM = 'https://cdn.jsdelivr.net/npm/web-ifc@0.0.57/';

/** Cómo se ve un elemento. `color` null = el color del propio modelo. */
export interface Pintura {
  color?: number | null;
  opacidad?: number;
  visible?: boolean;
  /**
   * Pieza a medio construir: se dibuja sólida solo hasta `corte` (coordenada del mundo sobre el eje
   * `eje`: 0 = X, 1 = Y vertical, 2 = Z) y lo que falta se ve como fantasma.
   */
  recorte?: { eje: 0 | 1 | 2; corte: number; fantasma: { color: number; opacidad: number } };
}

export interface VisorDemoHandle {
  /** Imagen PNG (data URL) de lo que se ve ahora. */
  capturar: () => string | null;
  /** Encuadra estos elementos (o todo el modelo si no se pasa nada). */
  encuadrar: (claves?: string[]) => void;
}

interface Props {
  fuentes: { clave: string; url: string }[];
  pintar?: (el: ElementoDemo | null, clave: string) => Pintura | undefined;
  onListo?: (modelos: Record<string, ModeloDemo>) => void;
  onPick?: (clave: string | null) => void;
  alto?: number | string;
  /** Dirección de la cámara (relativa al centro). */
  desde?: [number, number, number];
}

interface MallaDemo {
  mesh: any;
  clave: string;
  baseColor: any;
  baseOpacity: number;
}

// Mismo criterio que el visor de planos: menos de 5 px entre apretar y soltar es un clic, no un giro.
const PX_CLIC = 5;

export const DemoVisor3D = forwardRef<VisorDemoHandle, Props>(function DemoVisor3D(
  { fuentes, pintar, onListo, onPick, alto = 520, desde = [1, 0.75, 1.1] },
  ref,
) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [estado, setEstado] = useState<{ cargando: boolean; nota: string; error?: string }>({
    cargando: true,
    nota: 'Preparando el visor 3D…',
  });
  const mallasRef = useRef<MallaDemo[]>([]);
  const elementosRef = useRef<Map<string, ElementoDemo>>(new Map());
  const ctxRef = useRef<{ THREE: any; renderer: any; scene: any; camera: any; controls: any; root: any } | null>(null);
  const pintarRef = useRef(pintar);
  pintarRef.current = pintar;
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const onListoRef = useRef(onListo);
  onListoRef.current = onListo;
  const desdeRef = useRef(desde);
  desdeRef.current = desde;
  const [listo, setListo] = useState(false);
  const clavesFuentes = fuentes.map((f) => `${f.clave}=${f.url}`).join('|');

  /**
   * Planos de recorte de una malla (uno para lo construido y otro, opuesto, para el fantasma de lo que
   * falta). Se crean la primera vez que la pieza se recorta y después solo se mueven: cambiar la
   * cantidad de planos de un material obliga a recompilarlo.
   */
  const recortesDe = (m: MallaDemo) => {
    const c = ctxRef.current!;
    const { THREE } = c;
    let r = m.mesh.userData.recorte;
    if (!r) {
      const solido = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
      const resto = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
      m.mesh.material.clippingPlanes = [solido];
      m.mesh.material.needsUpdate = true;
      if (m.mesh.userData.aristas) {
        m.mesh.userData.aristas.material.clippingPlanes = [solido];
        m.mesh.userData.aristas.material.needsUpdate = true;
      }
      const matF = new THREE.MeshLambertMaterial({
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        clippingPlanes: [resto],
      });
      const fantasma = new THREE.Mesh(m.mesh.geometry, matF);
      fantasma.visible = false;
      m.mesh.add(fantasma);
      r = { solido, resto, fantasma };
      m.mesh.userData.recorte = r;
    }
    return r;
  };
  const LEJOS = 1e9;

  const aplicarPintura = () => {
    const f = pintarRef.current;
    for (const m of mallasRef.current) {
      const p = f ? f(elementosRef.current.get(m.clave) ?? null, m.clave) : undefined;
      const visible = p?.visible ?? true;
      m.mesh.visible = visible;
      if (!visible) continue;
      if (p?.recorte) {
        const r = recortesDe(m);
        const n = [0, 0, 0];
        n[p.recorte.eje] = 1;
        // sólido: se queda lo que está por DEBAJO del corte; fantasma: lo de arriba.
        r.solido.normal.set(-n[0]!, -n[1]!, -n[2]!);
        r.solido.constant = p.recorte.corte;
        r.resto.normal.set(n[0]!, n[1]!, n[2]!);
        r.resto.constant = -p.recorte.corte;
        r.fantasma.visible = true;
        r.fantasma.material.color.set(p.recorte.fantasma.color);
        r.fantasma.material.opacity = p.recorte.fantasma.opacidad;
      } else if (m.mesh.userData.recorte) {
        const r = m.mesh.userData.recorte;
        r.solido.normal.set(0, -1, 0);
        r.solido.constant = LEJOS;
        r.fantasma.visible = false;
      }
      const op = p?.opacidad ?? m.baseOpacity;
      m.mesh.material.color.set(p?.color != null ? p.color : m.baseColor);
      m.mesh.material.opacity = op;
      m.mesh.material.transparent = op < 1;
      m.mesh.material.depthWrite = op >= 0.6;
      if (m.mesh.userData.aristas) m.mesh.userData.aristas.visible = op >= 0.6;
    }
  };

  const encuadrar = (claves?: string[]) => {
    const c = ctxRef.current;
    if (!c) return;
    const { THREE, camera, controls, root } = c;
    const box = new THREE.Box3();
    const set = claves ? new Set(claves) : null;
    for (const m of mallasRef.current) {
      if (set ? !set.has(m.clave) : !m.mesh.visible) continue;
      box.expandByObject(m.mesh);
    }
    if (box.isEmpty()) box.setFromObject(root);
    const ctr = box.getCenter(new THREE.Vector3());
    const radio = Math.max(box.getSize(new THREE.Vector3()).length() / 2, 1);
    const dir = new THREE.Vector3(...desdeRef.current).normalize();
    const dist = radio / Math.sin((camera.fov * Math.PI) / 360);
    camera.position.copy(ctr).addScaledVector(dir, dist * (set ? 1.6 : 0.95));
    camera.near = Math.max(0.05, dist / 100);
    camera.far = dist * 20;
    camera.updateProjectionMatrix();
    controls.target.copy(ctr);
    controls.update();
  };

  useImperativeHandle(ref, () => ({
    capturar: () => {
      const c = ctxRef.current;
      if (!c) return null;
      c.renderer.render(c.scene, c.camera);
      return c.renderer.domElement.toDataURL('image/png');
    },
    encuadrar,
  }));

  useEffect(() => {
    if (listo) aplicarPintura();
  }, [pintar, listo]);

  useEffect(() => {
    let vivo = true;
    let limpiar: (() => void) | null = null;
    setListo(false);
    (async () => {
      const host = hostRef.current;
      if (!host) return;
      try {
        const THREE = await import(/* @vite-ignore */ THREE_URL);
        const { OrbitControls } = await import(/* @vite-ignore */ ORBIT_URL);
        const WebIFC: any = await import(/* @vite-ignore */ WEBIFC_URL);
        if (!vivo) return;
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x0d1117);
        const w0 = host.clientWidth || 800;
        const h0 = host.clientHeight || 500;
        const camera = new THREE.PerspectiveCamera(50, w0 / h0, 0.1, 5000);
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(w0, h0);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.localClippingEnabled = true; // piezas a medio construir (4D)
        host.appendChild(renderer.domElement);
        renderer.domElement.style.touchAction = 'none';
        scene.add(new THREE.HemisphereLight(0xffffff, 0x556070, 1.15));
        const luz = new THREE.DirectionalLight(0xffffff, 1.15);
        luz.position.set(30, 50, 25);
        scene.add(luz);
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        const root = new THREE.Group();
        scene.add(root);
        ctxRef.current = { THREE, renderer, scene, camera, controls, root };

        let raf = 0;
        const loop = () => {
          raf = requestAnimationFrame(loop);
          controls.update();
          renderer.render(scene, camera);
        };
        loop();
        const ro = new ResizeObserver(() => {
          const w = host.clientWidth || 800;
          const h = host.clientHeight || 500;
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h);
        });
        ro.observe(host);

        const raycaster = new THREE.Raycaster();
        const puntero = new THREE.Vector2();
        let abajo: { x: number; y: number } | null = null;
        const onDown = (e: PointerEvent) => {
          abajo = { x: e.clientX, y: e.clientY };
        };
        const onUp = (e: PointerEvent) => {
          if (!abajo) return;
          const movido = Math.hypot(e.clientX - abajo.x, e.clientY - abajo.y);
          abajo = null;
          if (movido > PX_CLIC || !onPickRef.current) return;
          const r = renderer.domElement.getBoundingClientRect();
          puntero.x = ((e.clientX - r.left) / r.width) * 2 - 1;
          puntero.y = -((e.clientY - r.top) / r.height) * 2 + 1;
          raycaster.setFromCamera(puntero, camera);
          const visibles = root.children.filter((m: any) => m.visible && m.material.opacity >= 0.3);
          const hit = raycaster.intersectObjects(visibles, false)[0];
          onPickRef.current((hit?.object?.userData?.clave as string | undefined) ?? null);
        };
        renderer.domElement.addEventListener('pointerdown', onDown);
        renderer.domElement.addEventListener('pointerup', onUp);
        limpiar = () => {
          cancelAnimationFrame(raf);
          ro.disconnect();
          renderer.domElement.removeEventListener('pointerdown', onDown);
          renderer.domElement.removeEventListener('pointerup', onUp);
          renderer.dispose();
          renderer.domElement.remove();
          ctxRef.current = null;
          mallasRef.current = [];
        };

        setEstado({ cargando: true, nota: 'Iniciando el motor IFC…' });
        const api = new WebIFC.IfcAPI();
        api.SetWasmPath(WEBIFC_WASM, true);
        await api.Init();
        if (!vivo) return;

        // Mismos umbrales de medición que el visor real (calibración); si no se pueden leer, los de fábrica.
        const parametros = await getParametrosMedicionIfc().catch(() => undefined);
        if (!vivo) return;
        const modelos: Record<string, ModeloDemo> = {};
        const mallas: MallaDemo[] = [];
        const elementos = new Map<string, ElementoDemo>();
        for (const fuente of fuentes) {
          setEstado({ cargando: true, nota: 'Descargando el modelo…' });
          const resp = await fetch(fuente.url);
          if (!resp.ok) throw new Error(`No se pudo descargar el modelo (${resp.status}).`);
          const buf = new Uint8Array(await resp.arrayBuffer());
          if (!vivo) return;
          setEstado({ cargando: true, nota: 'Leyendo y midiendo el modelo…' });
          const modelID = api.OpenModel(buf);
          const medidor = crearMedidor(api, WebIFC, modelID, parametros);
          const etiquetaDe = await medidor.inventariar();
          const rel = leerRelaciones(api, WebIFC, modelID);
          const cajas = new Map<number, any>();
          api.StreamAllMeshes(modelID, (flat: any) => {
            const eid = flat.expressID as number;
            const clave = `${fuente.clave}:${eid}`;
            const placed = flat.geometries;
            for (let i = 0; i < placed.size(); i++) {
              const pg = placed.get(i);
              const g = api.GetGeometry(modelID, pg.geometryExpressID);
              const verts = api.GetVertexArray(g.GetVertexData(), g.GetVertexDataSize());
              const idx = api.GetIndexArray(g.GetIndexData(), g.GetIndexDataSize());
              const pos = new Float32Array(verts.length / 2);
              const nor = new Float32Array(verts.length / 2);
              for (let v = 0, k = 0; v < verts.length; v += 6, k += 3) {
                pos[k] = verts[v];
                pos[k + 1] = verts[v + 1];
                pos[k + 2] = verts[v + 2];
                nor[k] = verts[v + 3];
                nor[k + 1] = verts[v + 4];
                nor[k + 2] = verts[v + 5];
              }
              const geo = new THREE.BufferGeometry();
              geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
              geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
              geo.setIndex(new THREE.BufferAttribute(new Uint32Array(idx), 1));
              medidor.acumular(eid, pos, idx, pg.flatTransformation);
              const c = pg.color;
              const baseColor = new THREE.Color(c.x, c.y, c.z);
              const baseOpacity = c.w;
              const mat = new THREE.MeshLambertMaterial({
                color: baseColor,
                transparent: baseOpacity < 1,
                opacity: baseOpacity,
                side: THREE.DoubleSide,
              });
              const m = new THREE.Mesh(geo, mat);
              m.matrixAutoUpdate = false;
              m.matrix.fromArray(pg.flatTransformation);
              m.userData.clave = clave;
              if (baseOpacity >= 1) {
                const aristas = new THREE.LineSegments(
                  new THREE.EdgesGeometry(geo, 35),
                  new THREE.LineBasicMaterial({ color: 0x14171b }),
                );
                m.add(aristas);
                m.userData.aristas = aristas;
              }
              root.add(m);
              m.updateMatrixWorld(true);
              const b = new THREE.Box3().setFromObject(m);
              const prev = cajas.get(eid);
              cajas.set(eid, prev ? prev.union(b) : b);
              mallas.push({ mesh: m, clave, baseColor, baseOpacity });
            }
          });
          const med = medidor.cerrar();
          const ordenNivel = new Map(med.niveles.map((n, i) => [n.id, i]));
          const lista: ElementoDemo[] = [];
          for (const capa of med.capas) {
            for (const inst of capa.instancias ?? []) {
              const d = rel.datosDe(inst.id);
              const nivelId = med.nivelDeElemento.get(inst.id) ?? null;
              const caja = cajas.get(inst.id);
              const el: ElementoDemo = {
                clave: `${fuente.clave}:${inst.id}`,
                fuente: fuente.clave,
                eid: inst.id,
                gid: d.gid,
                clase: rel.claseDe.get(inst.id) ?? capa.categoria ?? '',
                nombre: d.nombre || etiquetaDe.get(inst.id) || '',
                tipo: capa.capa,
                predef: d.predef,
                nivelId,
                nivelNombre: nivelId != null ? (med.niveles.find((n) => n.id === nivelId)?.nombre ?? null) : null,
                nivelOrden: nivelId != null ? (ordenNivel.get(nivelId) ?? null) : null,
                area: inst.area,
                longitud: inst.longitud,
                volumen: inst.volumen,
                estimado: !!inst.estimadoGeometria,
                geo: med.geometria.get(inst.id) ?? {},
                alojados: d.alojados,
                tieneCantidadesPropias: d.tieneCantidadesPropias,
                tieneTipo: d.tieneTipo,
                tieneMaterial: d.tieneMaterial,
                caja: caja
                  ? { min: [caja.min.x, caja.min.y, caja.min.z], max: [caja.max.x, caja.max.y, caja.max.z] }
                  : null,
              };
              lista.push(el);
              elementos.set(el.clave, el);
            }
          }
          modelos[fuente.clave] = {
            fuente: fuente.clave,
            elementos: lista,
            porClave: new Map(lista.map((e) => [e.clave, e])),
            niveles: med.niveles.filter((n) => n.elementos > 0),
            noSeConstruye: rel.noSeConstruye,
            espacios: rel.espacios,
          };
          api.CloseModel(modelID);
          if (!vivo) return;
        }
        mallasRef.current = mallas;
        elementosRef.current = elementos;
        root.updateMatrixWorld(true);
        encuadrar();
        setEstado({ cargando: false, nota: '' });
        setListo(true);
        onListoRef.current?.(modelos);
      } catch (e) {
        if (vivo) setEstado({ cargando: false, nota: '', error: e instanceof Error ? e.message : String(e) });
      }
    })();
    return () => {
      vivo = false;
      limpiar?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clavesFuentes]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: alto,
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        border: '1px solid var(--border-subtle)',
        background: '#0d1117',
      }}
    >
      <div ref={hostRef} style={{ position: 'absolute', inset: 0 }} />
      {(estado.cargando || estado.error) && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            color: 'var(--text-secondary)',
            fontSize: 'var(--fs-300)',
            background: 'rgba(13,17,23,0.85)',
          }}
        >
          {estado.error ? (
            <span style={{ color: 'var(--bad)' }}>No se pudo abrir el modelo: {estado.error}</span>
          ) : (
            <>
              <Spinner size="tiny" /> {estado.nota}
            </>
          )}
        </div>
      )}
    </div>
  );
});
